import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactSelect from 'react-select';
import { MdArrowBack, MdClose, MdSupportAgent, MdChatBubbleOutline } from 'react-icons/md';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import SocketService from '../services/socketService';
import axiosInstance from '../utils/axiosInstance';
import theme from '../styles/theme';

const Shell = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const MainLayout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const ListPane = styled.div`
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid #e5e7eb;
  background: #ffffff;

  @media (max-width: 768px) {
    width: 100%;
    display: ${(p) => (p.$hideOnMobile ? 'none' : 'flex')};
  }
`;

const ChatPane = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f8fafc;

  @media (max-width: 768px) {
    display: ${(p) => (p.$hideOnMobile ? 'none' : 'flex')};
  }
`;

const ListHeaderBar = styled.div`
  flex-shrink: 0;
  padding: 18px 18px 14px;
  border-bottom: 1px solid #f1f5f9;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 19px;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
`;

const NewIssueButton = styled.button`
  margin-top: 12px;
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  background: ${theme.gradient};
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(67, 56, 202, 0.25);
  transition: filter 0.15s ease, transform 0.15s ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const ListScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const IssueRow = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  border-left: 3px solid ${(p) => (p.$selected ? theme.primary : 'transparent')};
  padding: 14px 16px;
  background: ${(p) => (p.$selected ? theme.primaryLight : '#ffffff')};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$selected ? theme.primaryLight : '#f8fafc')};
  }
`;

const IssueTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const IssueSubject = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IssueNumber = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  color: #94a3b8;
`;

const CourseTag = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TypeTag = styled.span`
  display: inline-block;
  margin-top: 4px;
  border-radius: 999px;
  border: 1px solid ${theme.primarySoftBorder};
  background: ${theme.primaryLight};
  color: ${theme.primaryDark};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
`;

const StatusPill = styled.span`
  flex-shrink: 0;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${(p) => p.$bg || '#e5e7eb'};
  color: ${(p) => p.$color || '#374151'};
  white-space: nowrap;
`;

const IssuePreview = styled.div`
  font-size: 12.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IssueFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 11.5px;
  color: #94a3b8;
`;

const UnreadBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: ${theme.gradient};
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: #0f172a;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const EmptyText = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
`;

const ErrorState = styled(EmptyState)`
  color: #991b1b;
`;

const ChatHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const BackButton = styled.button`
  display: none;
  flex-shrink: 0;
  border: none;
  background: #f1f5f9;
  color: #374151;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const ChatHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatHeaderSubject = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChatHeaderMeta = styled.div`
  margin-top: 3px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-size: 12px;
  color: #94a3b8;
`;

const ChatHeaderCourse = styled.span`
  font-weight: 600;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
`;

const ChatScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IssueDescriptionBlock = styled.div`
  align-self: flex-start;
  max-width: 82%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  border-bottom-left-radius: 4px;
  padding: 12px 14px;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const DescriptionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.primaryDark};
  margin-bottom: 4px;
`;

const MessageBubble = styled.div`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  max-width: 82%;
  background: ${(p) => (p.$mine ? theme.gradient : '#ffffff')};
  color: ${(p) => (p.$mine ? '#ffffff' : '#1e293b')};
  border: ${(p) => (p.$mine ? 'none' : '1px solid #e2e8f0')};
  border-radius: 14px;
  border-bottom-right-radius: ${(p) => (p.$mine ? '4px' : '14px')};
  border-bottom-left-radius: ${(p) => (p.$mine ? '14px' : '4px')};
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const MessageMeta = styled.div`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  font-size: 11px;
  color: #94a3b8;
  margin-top: -6px;
`;

const ReplyRow = styled.form`
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
`;

const ReplyInput = styled.textarea`
  flex: 1;
  min-height: 42px;
  max-height: 120px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: ${theme.gradient};
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ClosedNotice = styled.div`
  flex-shrink: 0;
  margin: 0 16px calc(16px + env(safe-area-inset-bottom));
  padding: 12px 14px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
  text-align: center;
`;

const ChatEmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  padding: 24px;
  color: #94a3b8;

  svg {
    font-size: 44px;
    opacity: 0.5;
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ChatEmptyTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #475569;
`;

const ChatEmptyText = styled.p`
  margin: 0;
  font-size: 13px;
  max-width: 260px;
`;

// Bottom-sheet-on-mobile modal, used only for the "raise a new issue" form --
// composing a new conversation is a distinct action from reading one, so it
// stays a focused overlay rather than living inline in either pane.
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalCard = styled.div`
  position: relative;
  width: min(640px, 100%);
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.25);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
    padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
    animation: slideUp 0.25s ease;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const ModalGrabber = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 999px;
    background: #e2e8f0;
    margin: 0 auto 12px;
  }
`;

const ModalError = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const Field = styled.div`
  & + & {
    margin-top: 12px;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  height: 42px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
  }
`;

// react-select is unstyled by default -- these hook into its style-function
// API to match the rest of this modal's inputs (42px tall, 10px radius,
// same focus ring as Input/Textarea). menuPortalTarget below renders the
// dropdown into document.body so it isn't clipped by the modal's own
// overflow-y/max-height.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? theme.primary : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.14)' : 'none',
    '&:hover': { borderColor: state.isFocused ? theme.primary : '#e5e7eb' },
  }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 14 }),
  singleValue: (base) => ({ ...base, fontSize: 14, color: '#111827' }),
  input: (base) => ({ ...base, fontSize: 14 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? theme.primary : state.isFocused ? theme.primaryLight : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#111827',
    cursor: 'pointer',
  }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: 'hidden', zIndex: 20 }),
  menuPortal: (base) => ({ ...base, zIndex: 1300 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? theme.primary : '#94a3b8',
  }),
};

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
  }
`;

const SubmitButton = styled.button`
  margin-top: 14px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: ${theme.gradient};
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreateCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: #f1f5f9;
  color: #475569;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
  z-index: 2;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const CreateIntro = styled.div`
  text-align: center;
  padding: 0 28px 14px;
`;

const CreateIconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 13px;
  background: ${theme.gradient};
  color: #ffffff;
  font-size: 19px;
  display: grid;
  place-items: center;
  margin: 0 auto 10px;
  box-shadow: 0 10px 24px rgba(67, 56, 202, 0.28);
`;

const CreateTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #0f172a;
`;

const CreateSubtitle = styled.p`
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.4;
`;

const CreateCourseNote = styled.div`
  margin: 0 0 14px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${theme.primaryLight};
  border: 1px solid ${theme.primarySoftBorder};
  color: ${theme.primaryDark};
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;
`;

const FormDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 14px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const CharCount = styled.span`
  font-size: 11px;
  color: #94a3b8;
  flex-shrink: 0;
`;

const HelperText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
`;

const statusPalette = {
  open: { bg: '#fee2e2', color: '#b91c1c', label: 'Open' },
  'in-progress': { bg: '#dbeafe', color: '#1d4ed8', label: 'In Progress' },
  resolved: { bg: '#dcfce7', color: '#166534', label: 'Resolved' },
  closed: { bg: '#e5e7eb', color: '#374151', label: 'Closed' },
};

const getStatusDisplay = (status) => statusPalette[status] || statusPalette.open;

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Upserts one issue into the list and re-sorts by latest activity (matches
// the backend's ORDER BY) -- used by both the socket push and our own
// create/reply responses, so neither path needs a full list refetch.
const mergeIssueIntoList = (setIssues, incoming) => {
  setIssues((prev) => {
    const idx = prev.findIndex((item) => item.id === incoming.id);
    const next = idx === -1 ? [incoming, ...prev] : prev.map((item, i) => (i === idx ? { ...item, ...incoming } : item));
    return [...next].sort((a, b) => {
      const at = new Date(a.lastMessageAt || a.updatedAt || a.createdAt).getTime();
      const bt = new Date(b.lastMessageAt || b.updatedAt || b.createdAt).getTime();
      return bt - at;
    });
  });
};

export default function Contact() {
  const courseInfo = AuthService.getCourseInfo();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [issueTypes, setIssueTypes] = useState([]);
  const issueTypeOptions = useMemo(
    () => issueTypes.map((type) => ({ value: type.id, label: type.name })),
    [issueTypes]
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [issueTypeId, setIssueTypeId] = useState('');
  const selectedIssueTypeOption = useMemo(
    () => issueTypeOptions.find((option) => option.value === issueTypeId) || null,
    [issueTypeOptions, issueTypeId]
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [detailIssue, setDetailIssue] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState(null);

  const [hodName, setHodName] = useState(null);
  useEffect(() => {
    axiosInstance
      .get('/contact-admin/hod')
      .then((resp) => setHodName(resp?.data?.name || null))
      .catch(() => setHodName(null));
  }, []);
  const hodLabel = hodName ? `HOD ${hodName}` : 'HOD';

  const loadIssues = () => {
    setLoading(true);
    setError(null);
    axiosInstance
      .get('/contact-admin/issues')
      .then((resp) => {
        setIssues(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch((err) => {
        console.error('Failed to load contact-admin issues', err);
        const message = err?.response?.data?.message || "We couldn't load your issues right now.";
        setError(message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadIssues();
    axiosInstance
      .get('/contact-admin/issue-types')
      .then((resp) => setIssueTypes(Array.isArray(resp?.data) ? resp.data : []))
      .catch((err) => console.error('Failed to load contact-admin issue types', err));
  }, []);

  // Live updates only while this page is open: connect on mount, disconnect
  // on unmount, so there's no socket sitting idle on every other route. An
  // incoming issue is merged straight into local state -- no refetch.
  useEffect(() => {
    const studentId = AuthService.getUser()?.studentId;
    if (!studentId) return undefined;

    SocketService.connect(studentId);
    const off = SocketService.on('contact_admin_update', (payload) => {
      if (!payload?.issue) return;
      const { issue: incoming, messages: incomingMessages } = payload;

      mergeIssueIntoList(setIssues, incoming);

      setDetailIssue((prev) => {
        if (!prev || prev.id !== incoming.id) return prev;
        return {
          ...prev,
          ...incoming,
          messages: incomingMessages !== undefined ? incomingMessages : prev.messages,
        };
      });
    });

    return () => {
      off();
      SocketService.disconnect();
    };
  }, []);

  const openCreate = () => {
    setSubject('');
    setDescription('');
    setIssueTypeId('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const submitCreate = (e) => {
    e.preventDefault();
    const trimmedSubject = subject.trim();
    const trimmedDescription = description.trim();
    if (!issueTypeId) {
      setCreateError('Please select an issue type.');
      return;
    }
    if (!trimmedSubject || !trimmedDescription) {
      setCreateError('Please fill in both the subject and description.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    axiosInstance
      .post('/contact-admin/issues', {
        issueTypeId: Number(issueTypeId),
        subject: trimmedSubject,
        description: trimmedDescription,
        registrationId: courseInfo?.registrationId || undefined,
      })
      .then((resp) => {
        setCreateOpen(false);
        if (resp?.data) {
          mergeIssueIntoList(setIssues, resp.data);
          openDetail(resp.data.id);
        }
      })
      .catch((err) => {
        console.error('Failed to create contact-admin issue', err);
        const message = err?.response?.data?.message || 'Unable to submit your issue right now.';
        setCreateError(message);
      })
      .finally(() => setCreating(false));
  };

  const openDetail = (issueIdentifier) => {
    setDetailLoading(true);
    setDetailError(null);
    setReplyText('');
    setReplyError(null);
    setDetailIssue({ id: issueIdentifier, messages: null });
    axiosInstance
      .get(`/contact-admin/issues/${encodeURIComponent(issueIdentifier)}`)
      .then((resp) => {
        setDetailIssue(resp?.data || null);
        // Opening an issue marks admin messages read server-side; reflect
        // that in the list locally instead of refetching everything.
        if (resp?.data) {
          const { messages, ...issueSummary } = resp.data;
          mergeIssueIntoList(setIssues, { ...issueSummary, unreadCount: 0 });
        }
      })
      .catch((err) => {
        console.error('Failed to load contact-admin issue', err);
        const message = err?.response?.data?.message || "We couldn't load this issue right now.";
        setDetailError(message);
      })
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailIssue(null);
    setDetailError(null);
    setReplyText('');
    setReplyError(null);
  };

  const submitReply = (e) => {
    e.preventDefault();
    const trimmed = replyText.trim();
    if (!trimmed || !detailIssue) return;
    setSending(true);
    setReplyError(null);
    axiosInstance
      .post(`/contact-admin/issues/${encodeURIComponent(detailIssue.id)}/messages`, { message: trimmed })
      .then((resp) => {
        setDetailIssue(resp?.data || null);
        setReplyText('');
        if (resp?.data) {
          const { messages, ...issueSummary } = resp.data;
          mergeIssueIntoList(setIssues, issueSummary);
        }
      })
      .catch((err) => {
        console.error('Failed to send contact-admin reply', err);
        const message = err?.response?.data?.message || 'Unable to send your reply right now.';
        setReplyError(message);
      })
      .finally(() => setSending(false));
  };

  const isDetailClosed = detailIssue?.status === 'closed';

  const sortedIssues = useMemo(() => issues, [issues]);

  return (
    <StudentLayout title="Contact HOD" fullBleed>
      <Shell>
        <MainLayout>
          <ListPane $hideOnMobile={!!detailIssue}>
            <ListHeaderBar>
              <Title>Contact HOD</Title>
              <Subtitle>
                {hodName ? `Raise an issue and ${hodLabel} will follow up here.` : 'Raise an issue and the HOD will follow up here.'}
              </Subtitle>
              <NewIssueButton type="button" onClick={openCreate}>
                + Raise New Issue
              </NewIssueButton>
            </ListHeaderBar>

            <ListScroll>
              {loading && <Loader message="Loading your issues…" />}

              {!loading && error && (
                <ErrorState>
                  <EmptyTitle>Unable to load issues</EmptyTitle>
                  <EmptyText>{error}</EmptyText>
                </ErrorState>
              )}

              {!loading && !error && sortedIssues.length === 0 && (
                <EmptyState>
                  <EmptyTitle>No issues raised yet</EmptyTitle>
                  <EmptyText>
                    If something isn't working or you need help from the HOD, raise an issue and we'll get back to
                    you here.
                  </EmptyText>
                </EmptyState>
              )}

              {!loading && !error && sortedIssues.length > 0 &&
                sortedIssues.map((issue) => {
                  const statusDisplay = getStatusDisplay(issue.status);
                  return (
                    <IssueRow
                      key={issue.id}
                      type="button"
                      $selected={detailIssue?.id === issue.id}
                      onClick={() => openDetail(issue.id)}
                    >
                      <IssueTopRow>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <IssueSubject>{issue.subject}</IssueSubject>
                          <IssueNumber>{issue.id}</IssueNumber>
                          {(issue.courseShortName || issue.courseName) && (
                            <CourseTag title={issue.courseName || issue.courseShortName}>
                              {issue.courseShortName || issue.courseName}
                            </CourseTag>
                          )}
                          {issue.issueType && <TypeTag>{issue.issueType}</TypeTag>}
                        </div>
                        <StatusPill $bg={statusDisplay.bg} $color={statusDisplay.color}>
                          {statusDisplay.label}
                        </StatusPill>
                      </IssueTopRow>
                      <IssuePreview>{issue.lastMessage || issue.description}</IssuePreview>
                      <IssueFooterRow>
                        <span>Updated {formatDateTime(issue.updatedAt)}</span>
                        {issue.unreadCount > 0 && <UnreadBadge>{issue.unreadCount}</UnreadBadge>}
                      </IssueFooterRow>
                    </IssueRow>
                  );
                })}
            </ListScroll>
          </ListPane>

          <ChatPane $hideOnMobile={!detailIssue}>
            {!detailIssue && (
              <ChatEmptyState>
                <MdChatBubbleOutline />
                <ChatEmptyTitle>Select a conversation</ChatEmptyTitle>
                <ChatEmptyText>
                  Pick an issue from the list, or raise a new one, to start chatting with the HOD.
                </ChatEmptyText>
              </ChatEmptyState>
            )}

            {detailIssue && (
              <>
                <ChatHeader>
                  <BackButton type="button" onClick={closeDetail} aria-label="Back to issues">
                    <MdArrowBack />
                  </BackButton>
                  <ChatHeaderInfo>
                    <ChatHeaderSubject>{detailIssue.subject || 'Issue'}</ChatHeaderSubject>
                    <ChatHeaderMeta>
                      <span>Contact with {hodLabel}</span>
                      <span>·</span>
                      <span>{detailIssue.id}</span>
                      {(detailIssue.courseShortName || detailIssue.courseName) && (
                        <>
                          <span>·</span>
                          <ChatHeaderCourse title={detailIssue.courseName || detailIssue.courseShortName}>
                            {detailIssue.courseShortName || detailIssue.courseName}
                          </ChatHeaderCourse>
                        </>
                      )}
                      {detailIssue.issueType && (
                        <>
                          <span>·</span>
                          <TypeTag>{detailIssue.issueType}</TypeTag>
                        </>
                      )}
                    </ChatHeaderMeta>
                  </ChatHeaderInfo>
                  {detailIssue.status && (
                    <StatusPill
                      $bg={getStatusDisplay(detailIssue.status).bg}
                      $color={getStatusDisplay(detailIssue.status).color}
                    >
                      {getStatusDisplay(detailIssue.status).label}
                    </StatusPill>
                  )}
                </ChatHeader>

                {detailLoading && <Loader message="Loading conversation…" />}
                {detailError && (
                  <ChatScroll>
                    <ErrorState>
                      <EmptyTitle>Unable to load conversation</EmptyTitle>
                      <EmptyText>{detailError}</EmptyText>
                    </ErrorState>
                  </ChatScroll>
                )}

                {!detailLoading && !detailError && (
                  <ChatScroll>
                    {detailIssue.description && (
                      <IssueDescriptionBlock>
                        <DescriptionLabel>Original issue</DescriptionLabel>
                        {detailIssue.description}
                      </IssueDescriptionBlock>
                    )}

                    {Array.isArray(detailIssue.messages) &&
                      detailIssue.messages.map((msg) => (
                        <React.Fragment key={msg.id}>
                          <MessageBubble $mine={msg.sender === 'student'}>{msg.message}</MessageBubble>
                          <MessageMeta $mine={msg.sender === 'student'}>
                            {msg.sender === 'student' ? 'You' : hodLabel} · {formatDateTime(msg.timestamp)}
                          </MessageMeta>
                        </React.Fragment>
                      ))}
                  </ChatScroll>
                )}

                {!detailLoading && !detailError && isDetailClosed && (
                  <ClosedNotice>This issue is closed and no longer accepting replies.</ClosedNotice>
                )}

                {!detailLoading && !detailError && !isDetailClosed && (
                  <>
                    {replyError && (
                      <div style={{ padding: '0 16px' }}>
                        <ModalError>{replyError}</ModalError>
                      </div>
                    )}
                    <ReplyRow onSubmit={submitReply}>
                      <ReplyInput
                        placeholder="Type your reply…"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitReply(e);
                          }
                        }}
                      />
                      <SendButton type="submit" disabled={sending || !replyText.trim()}>
                        {sending ? 'Sending…' : 'Send'}
                      </SendButton>
                    </ReplyRow>
                  </>
                )}
              </>
            )}
          </ChatPane>
        </MainLayout>
      </Shell>

      {createOpen && (
        <ModalOverlay role="presentation" onClick={closeCreate}>
          <ModalCard role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <ModalGrabber />
            <CreateCloseButton type="button" onClick={closeCreate} aria-label="Close">
              <MdClose />
            </CreateCloseButton>

            <CreateIntro>
              <CreateIconBadge>
                <MdSupportAgent />
              </CreateIconBadge>
              <CreateTitle>Raise a new issue</CreateTitle>
              <CreateSubtitle>
                Tell admin what's going on and they'll reply right here in this conversation.
              </CreateSubtitle>
            </CreateIntro>

            <FormDivider />

            {courseInfo && (
              <CreateCourseNote>
                This issue will be tagged to: {courseInfo.courseShortName || courseInfo.courseName}
              </CreateCourseNote>
            )}

            {createError && <ModalError>{createError}</ModalError>}

            <form onSubmit={submitCreate}>
              <Field>
                <LabelRow>
                  <Label as="span">Issue type</Label>
                </LabelRow>
                <ReactSelect
                  inputId="issue-type"
                  value={selectedIssueTypeOption}
                  onChange={(option) => setIssueTypeId(option ? option.value : '')}
                  options={issueTypeOptions}
                  placeholder="Select an issue type…"
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  isSearchable
                />
              </Field>
              <Field>
                <LabelRow>
                  <Label as="span">Subject</Label>
                  <CharCount>{subject.length}/255</CharCount>
                </LabelRow>
                <Input
                  type="text"
                  placeholder="e.g. Unable to submit assignment"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={255}
                  autoFocus
                />
              </Field>
              <Field>
                <LabelRow>
                  <Label as="span">Description</Label>
                  <CharCount>{description.length}/2000</CharCount>
                </LabelRow>
                <Textarea
                  placeholder="Describe what happened, when it happened, and any error messages you saw..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
                <HelperText>
                  The more detail you give, the faster admin can help you.
                </HelperText>
              </Field>
              <SubmitButton type="submit" disabled={creating}>
                {creating ? 'Submitting…' : 'Submit Issue'}
              </SubmitButton>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </StudentLayout>
  );
}
